import { AllMemberTypes, BaseCompanyProps } from "@custom-types/base-types";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useAppDispatch } from "@store/store-helper";
import { removeMemberFromCompany } from "@store/member/member-thunks";
import { Dialog, useToast } from "@flat-ui";
import { useState } from "react";
import { MemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import { getMemberDisplayName } from "@utils/data-display";
import { LocationProp } from "@utils/track-event/event-types";

interface Props extends BaseCompanyProps, LocationProp {
  /** The member to be deleted from the company */
  member: AllMemberTypes;

  /** Whether the dialog is open */
  isOpen: boolean;

  /** Callback when the dialog should be closed */
  onClose: () => void;
}

/**
 * Shows a confirmation dialog to delete a member from a company.
 */
export function DeleteMemberFromCompany({
  member,
  company,
  isOpen,
  onClose,
  location,
}: Props): React.JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();
  const [isRemoving, setIsRemoving] = useState(false);
  const { trackEvent } = useTrackEvent();

  /** Reset the state of the dialog */
  function resetState(): void {
    setIsRemoving(false);
  }

  /**
   * Tracks the different events that occur during the delete member from company flow.
   */
  function trackDeleteMemberEvent(
    name:
      | MemberEvents.deleteMemberFromWorkspaceCancel
      | MemberEvents.deleteMemberFromWorkspaceConfirm
      | MemberEvents.deleteMemberFromWorkspace
  ): void {
    trackEvent({
      name,
      props: {
        memberId: member.identity,
        companyId: company.id,
        location,
      },
    });
  }

  /** When the user cancels the dialog is closed and the state in canceled */
  function onCancelClicked(): void {
    trackDeleteMemberEvent(MemberEvents.deleteMemberFromWorkspaceCancel);
    resetState();
    onClose();
  }

  /** When the user confirms the deletion */
  async function onConfirmClicked(): Promise<void> {
    trackDeleteMemberEvent(MemberEvents.deleteMemberFromWorkspaceConfirm);
    try {
      setIsRemoving(true);
      await dispatch(
        removeMemberFromCompany({
          companyId: company.id,
          userIdentity: member.identity,
        })
      ).unwrap();

      trackDeleteMemberEvent(MemberEvents.deleteMemberFromWorkspace);

      openToast({
        title: "Member successfully removed from workspace.",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `delete-member-from-company-${Date.now().toString()}`,
        title: "Could not delete member from workspace",
        error,
      });
    } finally {
      setIsRemoving(false);
      onClose();
    }
  }

  return (
    <Dialog
      title="Delete Member"
      open={isOpen}
      onCancel={onCancelClicked}
      onConfirm={onConfirmClicked}
      showSpinner={isRemoving}
    >
      <TextWithTooltip
        prefix="Are you sure you want to delete"
        text={getMemberDisplayName(member)}
        suffix={`from ${company.name} workspace?`}
      />
    </Dialog>
  );
}
